/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import { Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router";
import { useQuery, useLazyQuery } from "@apollo/client";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import moment from "moment";
import {
  GET_OPPORTUNITY_APPLICATIONS,
  GET_OPPORTUNITY_APPLICATIONS_NEXT,
  GET_OPPORTUNITY_APPLICATIONS_PREV,
} from "graphql/query/admin";
import Table from "Components/Table/Table";

const APPLICATION_STATUS_OPTIONS = ["pending", "accepted", "rejected"];

const InfoRow = ({ label, value }) => (
  <Grid container style={{ marginBottom: 10 }}>
    <Grid item xs={4} sm={3}>
      <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555" }}>{label}</Typography>
    </Grid>
    <Grid item xs={8} sm={9}>
      <Typography style={{ fontSize: 14 }}>{value || "N/A"}</Typography>
    </Grid>
  </Grid>
);

const MediaItem = ({ media, index }) => {
  const type = (media.mediaType || "").toLowerCase();
  if (type === "video") {
    return (
      <Box style={{ borderRadius: 8, overflow: "hidden", background: "#000" }}>
        <video
          src={media.url}
          controls
          style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }}
        />
        <Typography variant="caption" style={{ display: "block", padding: "4px 6px", color: "#ccc", background: "#111", fontSize: 11 }}>
          {media.name || `ref-${index}`}
        </Typography>
      </Box>
    );
  }
  return (
    <img
      src={media.url}
      alt={media.name || `ref-${index}`}
      style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 8, border: "1px solid #eee", display: "block" }}
    />
  );
};

const StatusChip = ({ status }) => {
  const colorMap = {
    open: { bg: "#e8f5e9", color: "#2e7d32" },
    closed: { bg: "#ffebee", color: "#c62828" },
    cancelled: { bg: "#f3e5f5", color: "#6a1b9a" },
  };
  const style = colorMap[status] || { bg: "#f5f5f5", color: "#333" };
  return (
    <Chip
      label={status || "N/A"}
      size="small"
      style={{ background: style.bg, color: style.color, fontWeight: 600 }}
    />
  );
};

const ApplicationStatusChip = ({ status }) => {
  const colorMap = {
    pending: { bg: "#fff8e1", color: "#f57f17" },
    accepted: { bg: "#e8f5e9", color: "#2e7d32" },
    rejected: { bg: "#ffebee", color: "#c62828" },
  };
  const style = colorMap[status] || { bg: "#f5f5f5", color: "#333" };
  return (
    <Chip
      label={status || "N/A"}
      size="small"
      style={{ background: style.bg, color: style.color, fontWeight: 600 }}
    />
  );
};

const ApplicationsSection = ({ opportunityId }) => {
  const [applications, setApplications] = useState([]);
  const [pageInfo, setPageInfo] = useState("");
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");

  const { data, refetch, error, loading } = useQuery(GET_OPPORTUNITY_APPLICATIONS, {
    variables: {
      opportunityId,
      first: 10,
      orderBy: "appliedAt_DESC",
      filters: appliedStatus ? { status: appliedStatus } : {},
    },
    fetchPolicy: "network-only",
    skip: !opportunityId,
  });

  const [getNextPage, { loading: nextLoader }] = useLazyQuery(GET_OPPORTUNITY_APPLICATIONS_NEXT);
  const [getPrevPage, { loading: prevLoader }] = useLazyQuery(GET_OPPORTUNITY_APPLICATIONS_PREV);

  const mapEdges = (edges) =>
    edges.map((e) => ({
      ...e.node,
      applicantName: e.node.applicant?.fullName || "N/A",
      appliedAtDisplay: e.node.appliedAt ? moment(e.node.appliedAt).format("DD/MM/YYYY HH:mm") : "N/A",
    }));

  const handleNextPage = async () => {
    if (pageInfo.hasNextPage) {
      const res = await getNextPage({
        variables: {
          opportunityId,
          first: 10,
          after: pageInfo.endCursor,
          orderBy: "appliedAt_DESC",
          filters: appliedStatus ? { status: appliedStatus } : {},
        },
        fetchPolicy: "network-only",
      });
      if (res.data?.opportunityApplications) {
        setApplications(mapEdges(res.data.opportunityApplications.edges));
        setPage(page + 1);
        setPageInfo(res.data.opportunityApplications.pageInfo);
      }
    }
  };

  const handlePreviousPage = async () => {
    if (pageInfo.hasPreviousPage) {
      const res = await getPrevPage({
        variables: {
          opportunityId,
          last: 10,
          before: pageInfo.startCursor,
          orderBy: "appliedAt_DESC",
          filters: appliedStatus ? { status: appliedStatus } : {},
        },
        fetchPolicy: "network-only",
      });
      if (res.data?.opportunityApplications) {
        setApplications(mapEdges(res.data.opportunityApplications.edges));
        setPage(page - 1);
        setPageInfo(res.data.opportunityApplications.pageInfo);
      }
    }
  };

  useEffect(() => {
    if (data?.opportunityApplications) {
      setApplications(mapEdges(data.opportunityApplications.edges));
      setPageInfo(data.opportunityApplications.pageInfo);
    }
    setPage(0);
  }, [data]);

  const handleApplyFilter = () => {
    setAppliedStatus(status);
    setTimeout(() => refetch(), 100);
  };

  const handleResetFilter = () => {
    setStatus("");
    setAppliedStatus("");
    setTimeout(() => refetch(), 100);
  };

  const TABLE_HEAD = [
    { id: "id", flag: "id", label: "No.", alignRight: false },
    { id: "applicantName", flag: "applicantName", label: "Applicant", alignRight: false },
    { id: "status", flag: "status", label: "Status", alignRight: false },
    { id: "message", flag: "message", label: "Message", alignRight: false },
    { id: "reason", flag: "reason", label: "Reason", alignRight: false },
    { id: "appliedAtDisplay", flag: "appliedAtDisplay", label: "Applied At", alignRight: false },
  ];

  return (
    <Box style={{ background: "#fff", borderRadius: 8, padding: 24, marginTop: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
      <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 16, gap: 12 }}>
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          Applications ({applications.length})
        </Typography>
        <Box style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {APPLICATION_STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" color="primary" size="small" onClick={handleApplyFilter} style={{ background: "var(--color-primary, #1976d2)" }}>
            Apply
          </Button>
          <Button variant="outlined" size="small" onClick={handleResetFilter}>
            Reset
          </Button>
        </Box>
      </Box>

      <Table
        error={error}
        USERLIST={applications.map((a) => ({ ...a, status: <ApplicationStatusChip status={a.status} /> }))}
        page={page}
        handleNextPage={handleNextPage}
        handlePreviousPage={handlePreviousPage}
        TABLE_HEAD={TABLE_HEAD}
        pageInfo={pageInfo}
        loading={loading || nextLoader || prevLoader}
      />
    </Box>
  );
};

const ViewOpportunity = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { opportunity, userId } = state || {};

  if (!opportunity) {
    return (
      <Container maxWidth={false} disableGutters style={{ margin: "0 2%" }}>
        <Typography>No opportunity data found.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters style={{ margin: "0 2%", minHeight: "70vh" }}>
      {/* Breadcrumb */}
      <Typography style={{ fontWeight: "bold", display: "flex", alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <Link to="/admin/user" style={{ color: "black", textDecoration: "none", marginRight: 4 }}>Users</Link>
        &gt;
        <span
          style={{ cursor: "pointer", marginLeft: 4, marginRight: 4 }}
          onClick={() => navigate("/admin/user/view-user", { state: { id: userId } })}
        >
          View User
        </span>
        &gt;
        <span style={{ fontWeight: "bold", marginLeft: 4 }}>Opportunity Detail</span>
      </Typography>

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
        variant="outlined"
        size="small"
      >
        Back
      </Button>

      {/* Detail Card */}
      <Box style={{ background: "#fff", borderRadius: 8, padding: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
        <Box style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {opportunity.title || "Opportunity Detail"}
          </Typography>
          <StatusChip status={opportunity.status} />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <InfoRow label="Title" value={opportunity.title} />
            <InfoRow label="Post Type" value={opportunity.postType} />
            <InfoRow label="Category" value={opportunity.category} />
            <InfoRow label="Role" value={opportunity.role} />
            <InfoRow label="Shoot Type" value={opportunity.shootType} />
            <InfoRow label="Event Type" value={opportunity.eventType} />
            <InfoRow label="Event Time" value={opportunity.eventTime} />
            <InfoRow label="Status" value={opportunity.status} />
            <InfoRow label="Location" value={opportunity.location} />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoRow
              label="Start Date"
              value={opportunity.startDate ? moment(opportunity.startDate).format("DD/MM/YYYY") : null}
            />
            <InfoRow
              label="End Date"
              value={opportunity.endDate ? moment(opportunity.endDate).format("DD/MM/YYYY") : null}
            />
            <InfoRow label="Budget" value={opportunity.budget !== null && opportunity.budget !== undefined ? `₹${opportunity.budget}` : null} />
            <InfoRow label="Day Rate" value={opportunity.dayRate !== null && opportunity.dayRate !== undefined ? `₹${opportunity.dayRate}` : null} />
            <InfoRow label="Experience Required" value={opportunity.experienceRequired} />
            <InfoRow label="Experience" value={opportunity.experience} />
            <InfoRow label="Travel Availability" value={opportunity.travelAvailability} />
            <InfoRow
              label="Portfolio Link"
              value={opportunity.portfolioLink ? (
                <a href={opportunity.portfolioLink} target="_blank" rel="noreferrer">{opportunity.portfolioLink}</a>
              ) : null}
            />
            <InfoRow
              label="Created At"
              value={opportunity.createdAt ? moment(opportunity.createdAt).format("DD/MM/YYYY HH:mm") : null}
            />
            <InfoRow
              label="Updated At"
              value={opportunity.updatedAt ? moment(opportunity.updatedAt).format("DD/MM/YYYY HH:mm") : null}
            />
          </Grid>
        </Grid>

        <Box style={{ marginTop: 16 }}>
          <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 4 }}>
            Description
          </Typography>
          <Typography style={{ fontSize: 14 }}>
            {opportunity.description || "N/A"}
          </Typography>
        </Box>

        {opportunity.bio && (
          <Box style={{ marginTop: 16 }}>
            <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 4 }}>
              Bio
            </Typography>
            <Typography style={{ fontSize: 14 }}>{opportunity.bio}</Typography>
          </Box>
        )}

        {/* Equipment Required */}
        {Array.isArray(opportunity.equipmentRequired) && opportunity.equipmentRequired.length > 0 && (
          <Box style={{ marginTop: 16 }}>
            <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 8 }}>
              Equipment Required
            </Typography>
            <Grid container spacing={1}>
              {opportunity.equipmentRequired.map((eq) => (
                <Grid item key={eq}>
                  <Chip label={eq} size="small" variant="outlined" />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Equipment Owned */}
        {Array.isArray(opportunity.equipmentOwned) && opportunity.equipmentOwned.length > 0 && (
          <Box style={{ marginTop: 16 }}>
            <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 8 }}>
              Equipment Owned
            </Typography>
            <Grid container spacing={1}>
              {opportunity.equipmentOwned.map((eq) => (
                <Grid item key={eq}>
                  <Chip label={eq} size="small" variant="outlined" />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Reference media */}
        {Array.isArray(opportunity.reference) && opportunity.reference.length > 0 && (
          <Box style={{ marginTop: 20 }}>
            <Typography style={{ fontWeight: 600, fontSize: 14, color: "#555", marginBottom: 8 }}>
              Reference Media ({opportunity.reference.length})
            </Typography>
            <Grid container spacing={2}>
              {opportunity.reference.map((media, i) => (
                <Grid item key={i} xs={6} sm={4} md={3}>
                  <MediaItem media={media} index={i} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>

      {/* Applications */}
      <ApplicationsSection opportunityId={opportunity.id} />
    </Container>
  );
};

export default ViewOpportunity;
